import React from 'react'
import { useSelector } from 'react-redux'
import classNames from 'classnames'
import OutsideClickHandler from 'react-outside-click-handler'

import { getUserPreferences } from 'data/userPreferences/selectors'
import {
  goToUserOnboardingStep,
  skipUserOnboarding,
} from 'data/onboarding/actions'
import { isOwnerOnboardingActive as isOwnerOnboardingActiveSelector } from 'screens/OnboardingPopover/utils'

import TinyPopover from 'components/Utils/TinyPopover'
import { LockIcon } from 'components/Utils/Icons'
import { Text, Button } from 'components/base'

import '../StepPopover.css'
import { useDispatch } from 'react-redux'

export default React.memo(function OnboardingStepPopover({
  step,
  position,
  popoverStyle,
  align,
  children,
}) {
  const isOwnerOnboardingActive = useSelector(isOwnerOnboardingActiveSelector)
  const onboardingStep = useSelector(state =>
    getUserPreferences(state).get('onboardingStep')
  )

  const [show, setShow] = React.useState(false)
  const [animateIn, setAnimateIn] = React.useState(false)
  const dispatch = useDispatch()

  const animationTimeout = React.useRef()
  const showTimeout = React.useRef()

  const canDisplay = React.useMemo(
    () => {
      if (isOwnerOnboardingActive) {
        return false
      }
      return step === onboardingStep
    },
    [step, isOwnerOnboardingActive, onboardingStep]
  )

  const steps = React.useMemo(
    () => ({
      channels: {
        icon: LockIcon,
        title: "Your team's channels",
        description:
          'Team docs are divided into channels to keep them organized. Start exploring or create your own!',
      },
    }),
    []
  )

  const stepKeys = React.useMemo(() => Object.keys(steps), [steps])

  const getNextStep = React.useCallback(
    step => {
      return stepKeys[stepKeys.indexOf(step) + 1] || 'done'
    },
    [stepKeys]
  )

  const onNextStep = React.useCallback(
    () => {
      dispatch(
        goToUserOnboardingStep({
          step: getNextStep(step),
        })
      )
    },
    [step, dispatch, getNextStep]
  )

  const handleOutsideClick = React.useCallback(
    () => {
      if (canDisplay) {
        onNextStep()
      }
    },
    [canDisplay, onNextStep]
  )

  const onSkip = React.useCallback(
    event => {
      event.preventDefault()
      event.stopPropagation()
      dispatch(skipUserOnboarding())
    },
    [dispatch]
  )

  const scheduleAnimation = React.useCallback(() => {
    animationTimeout.current = setTimeout(() => {
      setAnimateIn(true)
    }, 500)
  }, [])

  const scheduleShow = React.useCallback(
    () => {
      showTimeout.current = setTimeout(() => {
        setShow(true)
        scheduleAnimation()
      }, 500)
    },
    [scheduleAnimation]
  )

  React.useEffect(
    () => {
      if (canDisplay) {
        scheduleShow()
      }
      return () => {
        clearTimeout(showTimeout.current)
        clearTimeout(animationTimeout.current)
      }
    },
    [canDisplay, scheduleShow]
  )

  if (isOwnerOnboardingActive) {
    return children
  }

  return (
    <OutsideClickHandler onOutsideClick={handleOutsideClick}>
      <TinyPopover
        isOpen={show && canDisplay}
        containerStyle={{ overflow: 'visible', ...popoverStyle }}
        position={position}
        align={align}
        content={() => {
          return (
            <div
              style={{ width: 420 }}
              className={classNames(
                'onboarding-step-popover position-absolute padding-large background-primary radius-normal shade-100-important zindex-large',
                {
                  'onboarding-step-popover--in': animateIn,
                }
              )}
            >
              <div className={`onboarding-step-popover-arrow ${position}`} />
              <div className="flexbox flexbox-align-center margin-bottom-medium">
                <div
                  style={{
                    width: 28,
                    height: 28,
                    background: 'rgba(255, 255, 255, .3)',
                  }}
                  className="flexbox flexbox-align-center background-primary radius-round center-text margin-right-normal"
                >
                  {React.createElement(steps[step].icon, {
                    className: 'margin-left-auto margin-right-auto',
                    size: 12,
                  })}
                </div>
                <Text.Title className="shade-100-important">
                  {steps[step].title}
                </Text.Title>
              </div>
              <div className="flexbox flexbox-align-center font-size-large line-height-larger margin-bottom-medium">
                {steps[step].description}
              </div>
              <div className="flexbox flexbox-align-center">
                <Button.OnboardingPopover onClick={onNextStep}>
                  <div className="bold">
                    {stepKeys.indexOf(step) + 1 === stepKeys.length
                      ? "I'm all set"
                      : 'Next'}
                  </div>
                </Button.OnboardingPopover>
                {stepKeys.indexOf(step) + 1 !== stepKeys.length && (
                  <div className="margin-left-auto shade-05 opacity-normal shade-100-important">
                    I already know all this,{' '}
                    <span className="underline pointer" onClick={onSkip}>
                      skip all
                    </span>
                  </div>
                )}
              </div>
            </div>
          )
        }}
      >
        {children}
      </TinyPopover>
    </OutsideClickHandler>
  )
})
